import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Search, X, FileText, FileImage, FileSpreadsheet, File as FileIcon,
  ExternalLink, Download, Loader2, FolderOpen, Briefcase, ClipboardList,
  ClipboardCheck, User, Phone, MapPin, ArrowRight, CalendarDays,
} from 'lucide-react';
import AppLayout from '../components/AppLayout';
import { BRAND } from '../config/brand';
import app, { auth } from '../services/firebase-init';

// Search page over the Google Drive job archive (all years) plus live Hub
// records (jobs, estimates, job orders, customers). All Drive access goes
// through the archiveSearch / archiveFile Cloud Functions in
// functions/archive.js, so staff don't need their own Drive permissions.

const ARCHIVE_FILE_URL = 'https://us-central1-twp-calendar-61a9c.cloudfunctions.net/archiveFile';
const TYPE_DELAY_MS = 300;
const MIN_CHARS = 3; // shorter bits match thousands of files
const OVERLAY_DELAY_MS = 200; // quick searches finish without the overlay flashing

const INK    = '#2B2620';
const WALNUT = '#8B4513';
const RUST   = '#A13D2D';
const STONE  = '#8C8275';
const LINE   = 'rgba(43,38,32,0.10)';
const FONT_DISPLAY = "'Fraunces', Georgia, serif";
const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// Each year gets its own color so a result list reads at a glance.
const YEAR_COLORS = {
  2021: '#2F6F8F', 2022: '#6B4E9B', 2023: '#3B6B44',
  2024: '#A6752A', 2025: '#A13D2D', 2026: '#8B4513',
};
const EXTRA_YEAR_COLORS = ['#1F7A7A', '#7A3E6B', '#4F6A2B', '#9B5B1E'];
function yearColor(year) {
  if (!year || year === 'Unknown') return STONE;
  return YEAR_COLORS[year] || EXTRA_YEAR_COLORS[Number(year) % EXTRA_YEAR_COLORS.length];
}

// Color + icon per kind of result.
const HUB_TYPES = {
  'Job':       { color: '#8B4513', icon: Briefcase },
  'Estimate':  { color: '#A6752A', icon: ClipboardList },
  'Job Order': { color: '#4A5A9B', icon: ClipboardCheck },
  'Customer':  { color: '#3B6B44', icon: User },
};
function fileKind(mime = '') {
  if (mime.includes('pdf'))        return { label: 'PDF',   color: '#B23A2E', icon: FileText };
  if (mime.startsWith('image/'))   return { label: 'Image', color: '#1F7A7A', icon: FileImage };
  if (mime.includes('sheet') || mime.includes('excel') || mime.includes('csv'))
    return { label: 'Sheet', color: '#2E7D4F', icon: FileSpreadsheet };
  if (mime.includes('document') || mime.includes('word'))
    return { label: 'Doc', color: '#2F5FA8', icon: FileText };
  return { label: 'File', color: STONE, icon: FileIcon };
}

function previewKind(mime = '') {
  if (mime.startsWith('image/')) return 'image';
  if (mime.includes('pdf') || mime.startsWith('application/vnd.google-apps.')) return 'pdf';
  if (mime.startsWith('text/')) return 'pdf'; // browsers render text inline in an iframe
  return 'download';
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// ── File loading with prefetch ───────────────────────────────────────────────
// Hovering a result starts downloading it, so by the time it's clicked the
// file is usually already here. Kept for the life of the page.
const fileCache = new Map(); // id → Promise<Blob>
function loadFile(id) {
  if (!fileCache.has(id)) {
    const p = (async () => {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${ARCHIVE_FILE_URL}?id=${encodeURIComponent(id)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.blob();
    })();
    p.catch(() => fileCache.delete(id)); // let a failed load be retried
    fileCache.set(id, p);
  }
  return fileCache.get(id);
}

const searchFn = httpsCallable(getFunctions(app), 'archiveSearch');

export default function ArchivePage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const urlQuery = params.get('q') || '';
  const urlYear  = params.get('year') || '';

  const [input, setInput]         = useState(urlQuery);
  const [results, setResults]     = useState([]);
  const [records, setRecords]     = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [openFile, setOpenFile]   = useState(null);
  const [showInside, setShowInside] = useState(false);
  const [stats, setStats]         = useState(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const inputRef = useRef(null);

  // Landing-page numbers. Counted server-side once a day; the page works
  // fine without them.
  useEffect(() => {
    searchFn({ stats: true })
      .then(({ data }) => setStats(data))
      .catch(err => console.warn('[archiveStats]', err));
  }, []);

  useEffect(() => {
    if (!loading) { setShowOverlay(false); return; }
    const t = setTimeout(() => setShowOverlay(true), OVERLAY_DELAY_MS);
    return () => clearTimeout(t);
  }, [loading]);

  // Live search: the URL follows the box after a short pause in typing.
  useEffect(() => {
    const q = input.trim();
    if (q === urlQuery) return;
    const t = setTimeout(() => {
      if (q.length >= MIN_CHARS) setParams({ q }, { replace: true });
      else if (!q) setParams({}, { replace: true });
    }, TYPE_DELAY_MS);
    return () => clearTimeout(t);
  }, [input]); // eslint-disable-line react-hooks/exhaustive-deps

  // The URL is the source of truth, so back/forward and shared links work.
  // Old results stay on screen while the next search runs, so nothing flashes.
  useEffect(() => {
    if (document.activeElement !== inputRef.current) setInput(urlQuery);
    if (!urlQuery) { setResults([]); setRecords([]); setError(''); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setShowInside(false);
    searchFn({ q: urlQuery })
      .then(({ data }) => {
        if (cancelled) return;
        setError('');
        setResults(data.results || []);
        setRecords(data.records || []);
        setTruncated(!!data.truncated);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('[archiveSearch]', err);
        setResults([]);
        setRecords([]);
        setError(err.code === 'functions/permission-denied'
          ? 'Your account does not have archive access.'
          : 'Search is unavailable right now. Try again in a moment.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [urlQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  function submit(e) {
    e?.preventDefault();
    const q = input.trim();
    if (q) setParams({ q });
    inputRef.current?.blur();
  }

  function pickYear(year) {
    setParams(year ? { q: urlQuery, year } : { q: urlQuery }, { replace: true });
  }

  function clear() {
    setInput('');
    setParams({});
    inputRef.current?.focus();
  }

  // Year breakdown across Hub records and archive files, newest year first.
  const all = [...records, ...results];
  const yearCounts = {};
  for (const r of all) {
    const y = r.year || 'Unknown';
    yearCounts[y] = (yearCounts[y] || 0) + 1;
  }
  const years = Object.keys(yearCounts).sort((a, b) => b.localeCompare(a));
  const inYear = r => !urlYear || (r.year || 'Unknown') === urlYear;
  const visibleRecords = records.filter(inYear);
  const visibleFiles   = results.filter(inYear);
  const namedFiles     = visibleFiles.filter(f => f.nameMatch);
  const insideFiles    = visibleFiles.filter(f => !f.nameMatch);
  const hasQuery = !!urlQuery;
  const firstLoad = loading && all.length === 0;

  const searchBox = (
    <form onSubmit={submit} autoComplete="off" style={{ width: '100%', maxWidth: '720px', margin: '0 auto' }}>
      <div className="va-search" style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        background: 'white', borderRadius: '14px',
        padding: hasQuery ? '6px 6px 6px 16px' : '8px 8px 8px 20px',
        border: '1px solid rgba(43,38,32,0.18)',
        boxShadow: '0 2px 8px rgba(43,38,32,0.06)',
      }}>
        <Search size={19} color={STONE} style={{ flexShrink: 0 }} />
        <input
          ref={inputRef}
          autoFocus
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Last name, full name, phone number, or date"
          name="archive-search"
          style={{
            flex: 1, border: 'none', outline: 'none', background: 'transparent',
            fontSize: hasQuery ? '16px' : '17px', fontFamily: FONT_SANS, color: INK,
          }}
        />
        {input && (
          <button type="button" onClick={clear} aria-label="Clear search" style={iconBtn}>
            <X size={17} color={STONE} />
          </button>
        )}
        <button type="submit" className="va-go" style={{
          flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '8px',
          padding: hasQuery ? '10px 20px' : '13px 28px', borderRadius: '10px',
          background: WALNUT, color: 'white', border: 'none', cursor: 'pointer',
          fontSize: '15px', fontWeight: 600, fontFamily: FONT_SANS, letterSpacing: '0.01em',
        }}>
          <Search size={16} strokeWidth={2.4} /> Search
        </button>
      </div>
    </form>
  );

  return (
    <AppLayout title="Archive" breadcrumb="Dashboard › Archive">
      <div style={{ fontFamily: FONT_SANS, color: INK, maxWidth: '1040px', margin: '0 auto' }}>
        <div style={{ marginBottom: hasQuery ? '20px' : '0', paddingTop: hasQuery ? 0 : '8vh' }}>
          {!hasQuery && (
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <img src={BRAND.logoUrl} alt={BRAND.name} style={{ width: '88px', height: 'auto', marginBottom: '12px' }} />
              <h1 style={{ margin: 0, fontFamily: FONT_DISPLAY, fontSize: '34px', fontWeight: 600, color: INK }}>
                Archive
              </h1>
              <p style={{ margin: '6px 0 0', fontSize: '14px', color: STONE }}>
                Search every customer, job, and document on file.
              </p>
            </div>
          )}
          {searchBox}
          {!hasQuery && stats && <StatsLine stats={stats} />}
          {!hasQuery && <Tips fileCount={stats?.fileCount} />}
          {!hasQuery && error && <p style={{ textAlign: 'center', color: RUST, fontSize: '13.5px' }}>{error}</p>}
        </div>

        <div style={{ position: 'relative', minHeight: loading && hasQuery ? '360px' : 0 }}>
        {showOverlay && hasQuery && <SearchingOverlay query={urlQuery} fileCount={stats?.fileCount} />}
        {!hasQuery ? null : firstLoad ? (
          <SkeletonList />
        ) : error ? (
          <Banner color={RUST}>{error}</Banner>
        ) : all.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 16px', color: STONE }}>
            <Search size={36} color={`${WALNUT}55`} />
            <p style={{ margin: '12px 0 0', color: INK, fontWeight: 600, fontSize: '16px' }}>
              Nothing matched “{urlQuery}”
            </p>
            <p style={{ margin: '6px 0 0', fontSize: '13.5px' }}>
              Check the spelling, or try just the last name.
            </p>
          </div>
        ) : (
          <div>
            <Summary
              query={urlQuery}
              total={all.length}
              hubCount={records.length}
              fileCount={results.length}
              truncated={truncated}
              years={years}
              yearCounts={yearCounts}
              activeYear={urlYear}
              onPick={pickYear}
            />

            {visibleRecords.length > 0 && (
              <Section title="In the Hub" count={visibleRecords.length}>
                {visibleRecords.map(r => (
                  <RecordCard key={r.id} record={r} onOpen={() => r.link && navigate(r.link)} />
                ))}
              </Section>
            )}
            {namedFiles.length > 0 && (
              <Section title="Archive files" count={namedFiles.length}>
                {namedFiles.map(f => (
                  <FileCard key={f.id} file={f} onOpen={() => setOpenFile(f)} />
                ))}
              </Section>
            )}
            {insideFiles.length > 0 && (
              showInside || (namedFiles.length === 0 && visibleRecords.length === 0) ? (
                <Section title="Mentioned inside a document" count={insideFiles.length}>
                  {insideFiles.map(f => (
                    <FileCard key={f.id} file={f} onOpen={() => setOpenFile(f)} />
                  ))}
                </Section>
              ) : (
                <button type="button" onClick={() => setShowInside(true)} style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  color: WALNUT, fontSize: '13.5px', fontWeight: 600, fontFamily: FONT_SANS,
                }}>
                  Show {insideFiles.length} more where “{urlQuery}” only appears inside the document
                </button>
              )
            )}
          </div>
        )}
        </div>
      </div>

      {openFile && <FileViewer file={openFile} onClose={() => setOpenFile(null)} />}
      <style>{`
        @keyframes va-spin { to { transform: rotate(360deg); } }
        @keyframes va-fade { from { opacity: 0; } to { opacity: 1; } }
        .va-go:hover { background: #6B3410 !important; }
        .va-go:active { transform: translateY(1px); }
        @keyframes va-pulse { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }
        .va-card:hover { background: #FBF8F4 !important; border-color: rgba(139,69,19,0.25) !important; }
        .va-search:focus-within { border-color: ${WALNUT} !important; box-shadow: 0 0 0 3px ${WALNUT}1f !important; }
      `}</style>
    </AppLayout>
  );
}

const iconBtn = {
  background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px',
};

function StatsLine({ stats }) {
  const fmt = n => Number(n || 0).toLocaleString('en-US');
  const items = [
    stats.fileCount ? [fmt(stats.fileCount), 'documents in the archive'] : null,
    stats.firstYear ? [`${stats.firstYear}–${stats.lastYear}`, 'years on file'] : null,
    stats.hubCount ? [fmt(stats.hubCount), 'current Hub records'] : null,
  ].filter(Boolean);
  if (!items.length) return null;
  return (
    <div style={{
      display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '0',
      margin: '26px auto 0', maxWidth: '720px',
    }}>
      {items.map(([value, label], i) => (
        <div key={label} style={{
          padding: '4px 26px', textAlign: 'center',
          borderLeft: i ? '1px solid rgba(43,38,32,0.12)' : 'none',
        }}>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: '24px', fontWeight: 600, color: WALNUT }}>{value}</div>
          <div style={{ fontSize: '12.5px', color: STONE, marginTop: '2px' }}>{label}</div>
        </div>
      ))}
    </div>
  );
}

function Tips({ fileCount }) {
  const count = fileCount ? `${Math.floor(fileCount / 1000)},000+` : 'thousands of';
  const tips = [
    ['Search a whole name',
      'Type a last name like "Johnson", or first and last together. A few letters like "jo" match far too many files.'],
    ['Phone numbers',
      'With or without dashes. 605-432-1834 and 6054321834 both work.'],
    ['Dates',
      'Type it the way it would be written on the paperwork, like 6/14/2023 or June 14 2023.'],
    ['Give it a moment',
      `You're searching ${count} documents. Most searches take a few seconds, and the first one of the day can take a little longer.`],
  ];
  return (
    <div style={{ maxWidth: '720px', margin: '34px auto 0' }}>
      <div style={{
        fontSize: '12px', fontWeight: 700, color: STONE, textTransform: 'uppercase',
        letterSpacing: '0.06em', marginBottom: '10px',
      }}>
        Tips for better results
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '10px' }}>
        {tips.map(([title, text]) => (
          <div key={title} style={{
            background: 'white', border: `1px solid ${LINE}`, borderRadius: '12px', padding: '14px 16px',
          }}>
            <div style={{ fontWeight: 600, fontSize: '14px', color: INK }}>{title}</div>
            <div style={{ fontSize: '13px', color: STONE, marginTop: '4px', lineHeight: 1.5 }}>{text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SearchingOverlay({ query, fileCount }) {
  return (
    <div style={{
      position: 'absolute', inset: '-8px', zIndex: 5, borderRadius: '14px',
      background: 'rgba(250,247,243,0.55)',
      backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
      display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '70px',
      animation: 'va-fade 0.15s ease both',
    }}>
      <div role="status" aria-live="polite" style={{
        position: 'sticky', top: '90px',
        background: 'white', borderRadius: '16px', padding: '26px 34px',
        border: `1px solid ${LINE}`, boxShadow: '0 16px 40px rgba(43,38,32,0.16)',
        textAlign: 'center', minWidth: '280px',
      }}>
        <div style={{
          width: '38px', height: '38px', margin: '0 auto 14px', borderRadius: '50%',
          border: `3px solid ${WALNUT}22`, borderTopColor: WALNUT,
          animation: 'va-spin 0.8s linear infinite',
        }} />
        <div style={{ fontWeight: 600, fontSize: '15px', color: INK }}>Searching for “{query}”</div>
        <div style={{ fontSize: '13px', color: STONE, marginTop: '4px' }}>
          {fileCount
            ? `Looking through ${Number(fileCount).toLocaleString('en-US')} documents and the Hub`
            : 'Looking through the archive and the Hub'}
        </div>
      </div>
    </div>
  );
}

function Banner({ color, children }) {
  return (
    <div style={{
      background: `${color}10`, border: `1px solid ${color}40`, color,
      padding: '12px 16px', borderRadius: '12px', fontSize: '14px', fontWeight: 500,
    }}>
      {children}
    </div>
  );
}

function SkeletonList() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {[0, 1, 2, 3].map(i => (
        <div key={i} style={{
          height: '78px', borderRadius: '14px', background: 'white', border: `1px solid ${LINE}`,
          animation: `va-pulse 1.2s ease-in-out ${i * 0.12}s infinite`,
        }} />
      ))}
    </div>
  );
}

function Summary({ query, hubCount, fileCount, truncated, total, years, yearCounts, activeYear, onPick }) {
  const bits = [];
  if (hubCount) bits.push(`${hubCount} in the Hub`);
  if (fileCount) bits.push(`${fileCount}${truncated ? '+' : ''} archive file${fileCount === 1 ? '' : 's'}`);
  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ fontSize: '14px', color: STONE, marginBottom: years.length > 1 ? '12px' : 0 }}>
        <span style={{ color: INK }}>{bits.join(' and ')}</span> for “{query}”
      </div>
      {years.length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          <YearChip active={!activeYear} color={INK} onClick={() => onPick('')}>All ({total})</YearChip>
          {years.map(y => (
            <YearChip key={y} active={activeYear === y} color={yearColor(y)} onClick={() => onPick(y)}>
              {y} ({yearCounts[y]})
            </YearChip>
          ))}
        </div>
      )}
    </div>
  );
}

function YearChip({ active, color, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '5px 12px', borderRadius: '8px', cursor: 'pointer',
        border: `1px solid ${active ? color : 'rgba(43,38,32,0.14)'}`,
        background: active ? color : 'white',
        color: active ? 'white' : color,
        fontSize: '13px', fontWeight: 600, fontFamily: FONT_SANS,
        transition: 'all 0.12s ease',
      }}
    >
      {children}
    </button>
  );
}

function YearBadge({ year, title }) {
  if (!year) return null;
  const c = yearColor(year);
  return (
    <span title={title} style={{
      flexShrink: 0, padding: '2px 8px', borderRadius: '6px',
      background: `${c}14`, color: c, fontSize: '12.5px', fontWeight: 700,
    }}>
      {year}
    </span>
  );
}

function TypeTag({ color, children }) {
  return (
    <span style={{
      flexShrink: 0, padding: '2px 8px', borderRadius: '6px',
      background: `${color}14`, color, fontSize: '11px', fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.04em',
    }}>
      {children}
    </span>
  );
}

function Section({ title, count, children }) {
  return (
    <section style={{ marginBottom: '28px' }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '8px',
        fontSize: '12px', fontWeight: 700, color: STONE, textTransform: 'uppercase', letterSpacing: '0.06em',
      }}>
        {title} <span style={{ fontWeight: 500 }}>{count}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>{children}</div>
    </section>
  );
}

function Card({ color, onOpen, onHover, children }) {
  return (
    <button
      type="button"
      className="va-card"
      onClick={onOpen}
      onMouseEnter={onHover}
      style={{
        display: 'flex', alignItems: 'center', gap: '14px', textAlign: 'left',
        width: '100%', padding: '12px 14px', borderRadius: '10px',
        background: 'white', border: `1px solid ${LINE}`, borderLeft: `3px solid ${color}`,
        cursor: 'pointer', fontFamily: FONT_SANS,
        transition: 'background 0.12s ease, border-color 0.12s ease',
      }}
    >
      {children}
    </button>
  );
}

function CardIcon({ color, icon: Icon }) {
  return (
    <div style={{
      width: '36px', height: '36px', borderRadius: '8px', flexShrink: 0,
      background: `${color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon size={18} color={color} strokeWidth={1.8} />
    </div>
  );
}

const YEAR_SOURCE_LABEL = {
  folder:  'Filed in the {year} folder',
  name:    'Year taken from the file name',
  created: 'Year the file was added to Drive',
};

function FileCard({ file, onOpen }) {
  const kind = fileKind(file.mimeType);
  const crumbs = file.path.slice(1); // drop the archive root name
  return (
    <Card color={yearColor(file.year)} onOpen={onOpen} onHover={() => loadFile(file.id).catch(() => {})}>
      <CardIcon color={kind.color} icon={kind.icon} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <div style={{
            fontSize: '15.5px', fontWeight: 600, color: INK,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {file.name}
          </div>
          <TypeTag color={kind.color}>{kind.label}</TypeTag>
        </div>
        <div style={{
          fontSize: '12.5px', color: STONE, marginTop: '4px',
          display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap',
        }}>
          {crumbs.length > 0 && <><FolderOpen size={12} /> {crumbs.join(' › ')} <Dot /></>}
          <span>Modified {formatDate(file.modifiedTime)}</span>
          {file.size ? <><Dot /><span>{formatSize(file.size)}</span></> : null}
        </div>
      </div>
      <YearBadge year={file.year} title={YEAR_SOURCE_LABEL[file.yearSource]?.replace('{year}', file.year)} />
    </Card>
  );
}

function RecordCard({ record, onOpen }) {
  const t = HUB_TYPES[record.type] || HUB_TYPES.Job;
  return (
    <Card color={yearColor(record.year)} onOpen={onOpen}>
      <CardIcon color={t.color} icon={t.icon} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <div style={{
            fontSize: '15.5px', fontWeight: 600, color: INK,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {record.name}
          </div>
          <TypeTag color={t.color}>{record.type}</TypeTag>
          {record.status && (
            <span style={{ fontSize: '11.5px', color: STONE, textTransform: 'capitalize', flexShrink: 0 }}>
              {record.status.replace(/_/g, ' ')}
            </span>
          )}
        </div>
        <div style={{
          fontSize: '12.5px', color: STONE, marginTop: '4px',
          display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap',
        }}>
          {record.phone && <><Phone size={12} /> {record.phone}</>}
          {record.address && <>{record.phone && <Dot />}<MapPin size={12} /> {record.address}</>}
          {record.date && <>{(record.phone || record.address) && <Dot />}<CalendarDays size={12} /> {formatDate(record.date)}</>}
        </div>
      </div>
      <YearBadge year={record.year} />
      {record.link && <ArrowRight size={16} color={STONE} style={{ flexShrink: 0 }} />}
    </Card>
  );
}

function Dot() {
  return <span style={{ opacity: 0.5 }}>·</span>;
}

function FileViewer({ file, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError]     = useState('');
  const kind = previewKind(file.mimeType);
  const isGoogleDoc = file.mimeType.startsWith('application/vnd.google-apps.');
  const downloadName = isGoogleDoc ? `${file.name}.pdf` : file.name;

  useEffect(() => {
    let url;
    let cancelled = false;
    loadFile(file.id)
      .then(blob => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      })
      .catch(err => {
        console.error('[archiveFile]', err);
        if (!cancelled) setError('Could not open this file.');
      });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [file.id]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function download() {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = downloadName;
    a.click();
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(20,16,12,0.78)',
        backdropFilter: 'blur(4px)',
        display: 'flex', flexDirection: 'column', padding: '20px', fontFamily: FONT_SANS,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'white', marginBottom: '12px' }}
      >
        <YearBadge year={file.year} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '15px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {file.name}
          </div>
          <div style={{ fontSize: '12px', opacity: 0.7 }}>{file.path.slice(1).join(' › ')}</div>
        </div>
        {blobUrl && <ViewerBtn onClick={download}><Download size={15} /> Download</ViewerBtn>}
        {file.webViewLink && (
          <ViewerBtn as="a" href={file.webViewLink}><ExternalLink size={15} /> Open in Drive</ViewerBtn>
        )}
        <ViewerBtn onClick={onClose} aria-label="Close"><X size={16} /></ViewerBtn>
      </div>

      <div
        onClick={e => e.stopPropagation()}
        style={{
          flex: 1, background: 'white', borderRadius: '12px', overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {error ? (
          <p style={{ color: RUST }}>{error}</p>
        ) : !blobUrl ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: STONE }}>
            <Loader2 size={18} style={{ animation: 'va-spin 0.9s linear infinite' }} /> Opening…
          </div>
        ) : kind === 'image' ? (
          <img src={blobUrl} alt={file.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        ) : kind === 'pdf' ? (
          <iframe src={blobUrl} title={file.name} style={{ width: '100%', height: '100%', border: 'none' }} />
        ) : (
          <div style={{ textAlign: 'center', color: INK }}>
            <p style={{ margin: '0 0 12px' }}>This file type can&apos;t be previewed here.</p>
            <button
              onClick={download}
              style={{
                padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                background: WALNUT, color: 'white', fontWeight: 600, fontFamily: FONT_SANS,
              }}
            >
              Download {file.name}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ViewerBtn({ as = 'button', children, ...props }) {
  const Tag = as;
  return (
    <Tag
      {...props}
      {...(as === 'a' ? { target: '_blank', rel: 'noreferrer' } : { type: 'button' })}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        padding: '8px 12px', borderRadius: '8px', cursor: 'pointer',
        background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.22)',
        color: 'white', fontSize: '13px', fontWeight: 600, fontFamily: FONT_SANS,
        textDecoration: 'none',
      }}
    >
      {children}
    </Tag>
  );
}
